import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import PropTypes from 'prop-types';
import {
  Drawer, Select, Table, message,
} from 'antd';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';

import DrawerSubmitFooter from '../../../common/containers/DrawerSubmitFooter';
import DisplayText from '../../../common/text/DisplayText';

import Colors from '../../../constants/Colors';
import { getEquipmentTypes } from '../../../equipment/state/equipment.actions';

import { EQUIPMENT_NATIVE_FIELDS, ECLIPSE_EQUIPMENT_FIELDS } from './eclipse.constants';

const ECLIPSE_FIELD_OPTIONS = ECLIPSE_EQUIPMENT_FIELDS.map((field) => ({
  label: field.label,
  value: field.key,
}));

const ECLIPSE_FIELD_KEYS = new Set(ECLIPSE_EQUIPMENT_FIELDS.map((field) => field.key));

const MAPPABLE_CUSTOM_TYPES = new Set([
  'text',
]);

const getMappableType = (field) => {
  const { selectedType } = field;
  if (!MAPPABLE_CUSTOM_TYPES.has(selectedType)) return null;
  return selectedType;
};

const emptyMappings = () => ({
  types: {},
});

function EclipseEquipmentFieldMappingDrawer({
  visible,
  onClose,
  equipmentMappings,
  onSaved,
}) {
  const dispatch = useDispatch();
  const equipmentTypes = useSelector((state) => state.equipment.equipmentTypes);
  const [mappings, setMappings] = useState(emptyMappings);
  const [saving, setSaving] = useState(false);
  const [prevVisible, setPrevVisible] = useState(visible);

  const noneType = useMemo(
    () => (equipmentTypes ?? []).find((type) => type.isDefault),
    [equipmentTypes],
  );
  const noneTypeId = noneType?.id;
  const selectedFields = mappings.types?.[noneTypeId]?.fields ?? {};

  if (prevVisible !== visible) {
    if (visible) {
      setMappings({
        types: equipmentMappings?.types ?? {},
      });
    }
    setPrevVisible(visible);
  }

  useEffect(() => {
    if (visible) dispatch(getEquipmentTypes());
  }, [visible, dispatch]);

  const rows = useMemo(() => {
    const customRows = [];
    const sections = noneType?.customFields?.sections ?? [];
    sections.forEach((section) => {
      if (section.hidden) return;
      const { fields = [], name: sectionName } = section;
      fields?.forEach((field) => {
        if (!getMappableType(field)) return;
        const title = field.configProps?.title;
        if (!field.id || !title) return;
        customRows.push({
          key: field.id,
          label: sectionName ? `${sectionName} - ${title}` : title,
        });
      });
    });
    return EQUIPMENT_NATIVE_FIELDS.concat(customRows);
  }, [noneType]);

  const onFieldChange = useCallback((fieldKey, eclipseField) => {
    if (!noneTypeId) return;
    setMappings((current) => {
      const currentType = current.types?.[noneTypeId] ?? {};
      const fields = { ...(currentType.fields ?? {}) };
      if (eclipseField) fields[fieldKey] = eclipseField;
      else delete fields[fieldKey];
      return {
        types: {
          ...current.types,
          [noneTypeId]: {
            fields,
          },
        },
      };
    });
  }, [noneTypeId]);

  const columns = useMemo(() => ([
    {
      title: 'Ontraccr Field',
      dataIndex: 'label',
      width: '50%',
    },
    {
      title: 'Eclipse Field',
      dataIndex: 'key',
      width: '50%',
      render: (fieldKey) => (
        <Select
          style={{ width: '100%' }}
          options={ECLIPSE_FIELD_OPTIONS}
          value={selectedFields[fieldKey]}
          placeholder="Select Eclipse field"
          showSearch
          optionFilterProp="label"
          allowClear
          onChange={(eclipseField) => onFieldChange(fieldKey, eclipseField)}
        />
      ),
    },
  ]), [selectedFields, onFieldChange]);

  const onSubmit = useCallback(async () => {
    if (!noneTypeId) {
      message.error('None equipment type was not found');
      return;
    }
    const fields = {};
    const typeFields = mappings.types?.[noneTypeId]?.fields ?? {};
    Object.keys(typeFields).forEach((fieldKey) => {
      const eclipseField = typeFields[fieldKey];
      if (ECLIPSE_FIELD_KEYS.has(eclipseField)) fields[fieldKey] = eclipseField;
    });

    const payload = Object.keys(fields).length
      ? { types: { [noneTypeId]: { fields } } }
      : null;

    setSaving(true);
    try {
      await axios.put('/eclipse/config', { equipmentMappings: payload });
      message.success('Equipment field mappings saved');
      onSaved(payload);
      onClose();
    } catch (err) {
      message.error('Failed to update Eclipse config');
    } finally {
      setSaving(false);
    }
  }, [mappings, noneTypeId, onSaved, onClose]);

  return (
    <Drawer
      title="Configure Equipment Field Mappings"
      visible={visible}
      onClose={onClose}
      width={720}
      footer={(
        <DrawerSubmitFooter
          onClose={onClose}
          onSubmit={onSubmit}
          submitTitle="Save"
          loading={saving}
        />
      )}
    >
      <DisplayText
        title="Map Eclipse fields to equipment name, ID, and custom fields on the None equipment type."
        style={{
          marginBottom: 16,
          color: Colors.ONTRACCR_OPACITY_GRAY,
        }}
      />
      <Table
        size="small"
        pagination={false}
        rowKey="key"
        columns={columns}
        dataSource={rows}
      />
    </Drawer>
  );
}

EclipseEquipmentFieldMappingDrawer.propTypes = {
  visible: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired,
  equipmentMappings: PropTypes.shape({
    types: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  }),
};

EclipseEquipmentFieldMappingDrawer.defaultProps = {
  visible: false,
  equipmentMappings: null,
};

export default EclipseEquipmentFieldMappingDrawer;
